<script setup>

import { computed, onMounted, ref } from 'vue';
import { useNotificationStore } from '@/stores/NotificationStore.js'
import ButtonWithConfirmation from "../../../components/buttons/buttonWithConfirmation.vue";
const notificationStore = useNotificationStore()

const tags = ref([])
const showingTagSlideout = ref(false)
const savingTag = ref(false)
const editingTagId = ref(null)
const tagForm = ref({
    name: '',
    description: '',
    color: null,
})
const searchTerm = ref('')
const showSystemTags = ref(false)

const isEditing = computed(() => editingTagId.value !== null)

const loadTags = async () => {
    const res = await axios.get(route('crm.leadtags.index'))
    tags.value = res.data.tags;
}

const resetTagForm = () => {
    tagForm.value = {
        name: '',
        description: '',
        color: null,
    }
}

const setColor = (color) => {
    tagForm.value.color = color;
}

const openCreateTagSlideout = () => {
    resetTagForm()
    editingTagId.value = null
    showingTagSlideout.value = true
}

const openEditTagSlideout = (tag) => {
    tagForm.value = {
        name: tag.name ?? '',
        description: tag.description ?? '',
        color: tag.color ?? null,
    }
    editingTagId.value = tag.id
    showingTagSlideout.value = true
}

const closeTagSlideout = () => {
    showingTagSlideout.value = false
}

const saveTag = async () => {
    if (savingTag.value) {
        return
    }

    const trimmedName = tagForm.value.name.trim()
    const trimmedDescription = tagForm.value.description ? tagForm.value.description.trim() : ''

    if (trimmedName === '') {
        return
    }

    savingTag.value = true
    const tagName = trimmedName

    try {
        let res
        if (isEditing.value) {
            res = await axios.put(route('crm.leadtags.update', editingTagId.value), {
                name: trimmedName,
                description: trimmedDescription,
                color: tagForm.value.color,
            })
        } else {
            res = await axios.post(route('crm.leadtags.store'), {
                name: trimmedName,
                description: trimmedDescription,
                color: tagForm.value.color,
            })
        }

        if (res.data.error) {
            notificationStore.addNotification({
                type: 'error',
                title: _mft('error:error.generalOccurred'),
                message: res.data.error,
                dismissAfter: 2500,
            })
            return;
        }

        await loadTags()
        closeTagSlideout()
        notificationStore.addNotification({
            type: 'success',
            title: tagName,
            message: isEditing.value ? _mft('crm:tag.updated') : _mft('crm:tag.created'),
            dismissAfter: 2500,
        })
        resetTagForm()
    } catch (error) {
        notificationStore.addNotification({
            type: 'error',
            title: _mft('error:domainNameSingular'),
            message: _mft('error:error.generalTryAgain'),
            dismissAfter: 2500,
        })
    } finally {
        savingTag.value = false
    }
}

const deleteTag = async (tag) => {
    try {
        await axios.delete(route('crm.leadtags.delete', tag.id))

        await loadTags()
        notificationStore.addNotification({
            type: 'success',
            title: tag.name,
            message: _mft('crm:tag.deleted'),
            dismissAfter: 2500,
        })
    } catch (error) {
        notificationStore.addNotification({
            type: 'error',
            title: _mft('error:domainNameSingular'),
            message: _mft('error:error.generalTryAgain'),
            dismissAfter: 2500,
        })
    }
}

const updateColor = (color) => {
    tagForm.value.color = color;
}

const filteredTags = computed(() => {
    let _tags = tags.value;

    if (!showSystemTags.value) {
        _tags = _tags.filter(tag => !tag.is_system_tag);
    }

    if (searchTerm.value.trim() === '') {
        return _tags;
    }

    return _tags.filter(tag => {
        return tag.name.toLowerCase().includes(searchTerm.value.trim().toLowerCase());
    });
})

onMounted(() => {
    loadTags();
})

</script>
<template>
    <div>
        <div class="md:sticky top-2 z-topbar">
            <frosted-bar>
                <div class="flex items-center justify-between space-x-4">
                    <h1>{{ _mft('crm:leadIndex.columnLabel.tags') }}</h1>

                    <div>
                        <div class="flex items-center justify-end space-x-2">
                            <button-primary @click="openCreateTagSlideout">{{ _mft('crm:tag.createNew') }}</button-primary>
                        </div>
                    </div>
                </div>
            </frosted-bar>
        </div>

        <div class="mt-8">
            <div class="flex justify-between items-center">
                <toggle-switch
                    v-model:content="showSystemTags"
                    :label="_mft('crm:tag.showSystemTags')"
                    class="min-w-44"
                ></toggle-switch>

                <input-text
                    v-model:content="searchTerm"
                    :placeholder="_mft('shared:search')"
                />
            </div>

            <list-basic >
                <list-basic-item
                    v-for="(tag, i) in filteredTags"
                    :key="i"
                >
                    <template #title>
                        {{ tag.name }}
                    </template>

                    <template #status>
                        <div class="w-6">
                            <div
                                v-if="tag.color"
                                class="w-4 h-4 rounded-full mt-0.5"
                                :style="{backgroundColor: tag.color, color: tag.textcolor}"
                            ></div>
                        </div>
                    </template>

                    <div
                        v-if="tag.description"
                        class="text-xs text-gray-600 mt-1 mb-2"
                    >
                        {{ tag.description }}
                    </div>

                    <div class="text-xs space-y-1 mt-0.5">
                        <div class="flex space-x-6">
                            <component
                                :is="tag.leads_count > 0 ? 'a' : 'div'"
                                :href="route('crm.leads')+'?tag-ids='+tag.id"
                                :class="tag.leads_count > 0 ? 'mf-link' : ''"
                            >
                                {{ _mft('crm:lead.domainName', {count: tag.leads_count}) }}
                            </component>
                            <div>
                                {{ _mft('communication:automation.domainName', {count: tag.automations_count}) }}
                            </div>
                        </div>
                        <div class="text-gray-400 text-xxs">
                            {{ _mft('shared:date.created')}} {{ tag.created_at_user_tz.display }}
                        </div>
                    </div>

                    <template #actions>
                        <div class="flex flex-col items-end space-y-1">
                            <span
                                v-if="tag.is_system_tag"
                                class="text-gray-400 text-xs cursor-default"
                            >
                                {{ _mft('crm:tag.systemTag') }}
                            </span>
                            <button-secondary
                                v-if="tag.can_be_edited"
                                size="sm"
                                @click="openEditTagSlideout(tag)"
                            >
                                {{ _mft('shared:action.edit') }}
                            </button-secondary>

                            <button-with-confirmation
                                v-if="tag.can_be_deleted"
                                @confirmed="deleteTag(tag)"
                                size="sm"
                                style-type="danger"
                                modalClass="absolute -top-2 -right-2 p-2 bg-white text-black rounded shadow-md z-10"
                                :buttonLabel="_mft('shared:action.delete')"
                                :confirmButtonLabel="_mft('shared:action.delete')"
                                :confirm-text="_mft('shared:confirm.areYouSure')"
                            ></button-with-confirmation>
                        </div>
                    </template>
                </list-basic-item>
            </list-basic>
        </div>

        <slideout-with-slots
            v-if="showingTagSlideout"
            :title="isEditing ? _mft('shared:action.edit') : _mft('crm:tag.createNew')"
            @close="closeTagSlideout"
        >
            <template #body>
                <form
                    class="space-y-4"
                    id="tag-form"
                    @submit.prevent="saveTag"
                >
                    <div>
                        <input-text
                            v-model:content="tagForm.name"
                            :label="_mft('shared:name')"
                            name="name"
                            maxlength="36"
                            required
                            autofocus
                            class="!mb-1"
                            auto-complete="off"
                        />
                        <div class="text-xxs text-gray-400 text-right">
                            {{ tagForm.name.length > 0 ? tagForm.name.length + '/36' : '' }}
                        </div>
                    </div>
                    <div>
                        <input-text
                            v-model:content="tagForm.description"
                            :label="_mft('shared:description')"
                            name="description"
                            maxlength="100"
                            class="!mb-1"
                        />
                        <div class="text-xxs text-gray-400 text-right">
                            {{ tagForm.description.length > 0 ? tagForm.description.length + '/100' : '' }}
                        </div>
                    </div>

                    <div class="grid grid-cols-5 gap-2">
                        <div>
                            <button
                                @click.prevent="setColor('#C87878')"
                                class="w-full aspect-square rounded-wl border-2 transition-transform"
                                :class="tagForm.color === '#C87878' ? 'border-gray-600 scale-90' : 'border-transparent'"
                                style="background-color: #C87878"
                            ></button>
                        </div>
                        <div>
                            <button
                                @click.prevent="setColor('#f9fd9c')"
                                class="w-full aspect-square rounded-wl border-2 transition-transform"
                                :class="tagForm.color === '#f9fd9c' ? 'border-gray-600 scale-90' : 'border-transparent'"
                                style="background-color: #f9fd9c"
                            ></button>
                        </div>
                        <div>
                            <button
                                @click.prevent="setColor('#7BAF7A')"
                                class="w-full aspect-square rounded-wl border-2 transition-transform"
                                :class="tagForm.color === '#7BAF7A' ? 'border-gray-600 scale-90' : 'border-transparent'"
                                style="background-color: #7BAF7A"
                            ></button>
                        </div>

                        <div>
                            <button
                                @click.prevent="setColor('#7da9e2')"
                                class="w-full aspect-square rounded-wl border-2 transition-transform"
                                :class="tagForm.color === '#7da9e2' ? 'border-gray-600 scale-90' : 'border-transparent'"
                                style="background-color: #7da9e2"
                            ></button>
                        </div>
                        <div>
                            <button
                                @click.prevent="setColor('#E3D38B')"
                                class="w-full aspect-square rounded-wl border-2 transition-transform"
                                :class="tagForm.color === '#E3D38B' ? 'border-gray-600 scale-90' : 'border-transparent'"
                                style="background-color: #E3D38B"
                            ></button>
                        </div>
                        <div>
                            <button
                                @click.prevent="setColor('#ff9b69')"
                                class="w-full aspect-square rounded-wl border-2 transition-transform"
                                :class="tagForm.color === '#ff9b69' ? 'border-gray-600 scale-90' : 'border-transparent'"
                                style="background-color: #ff9b69"
                            ></button>
                        </div>
                        <div>
                            <button
                                @click.prevent="setColor('#9B85B5')"
                                class="w-full aspect-square rounded-wl border-2 transition-transform"
                                :class="tagForm.color === '#9B85B5' ? 'border-gray-600 scale-90' : 'border-transparent'"
                                style="background-color: #9B85B5"
                            ></button>
                        </div>
                        <div>
                            <button
                                @click.prevent="setColor('#82c6c0')"
                                class="w-full aspect-square rounded-wl border-2 transition-transform"
                                :class="tagForm.color === '#82c6c0' ? 'border-gray-600 scale-90' : 'border-transparent'"
                                style="background-color: #82c6c0"
                            ></button>
                        </div>
                        <div>
                            <button
                                @click.prevent="setColor('#fcbbe7')"
                                class="w-full aspect-square rounded-wl border-2 transition-transform"
                                :class="tagForm.color === '#fcbbe7' ? 'border-gray-600 scale-90' : 'border-transparent'"
                                style="background-color: #fcbbe7"
                            ></button>
                        </div>
                        <div>
                            <button
                                @click.prevent="setColor(null)"
                                class="w-full aspect-square rounded-wl border-2 bg-gray-200 transition-transform"
                                :class="tagForm.color === null ? 'border-gray-600 scale-90' : 'border-transparent'"
                            ></button>
                        </div>
                    </div>
                </form>
            </template>
            <template #actions>
                <div class="mb-2 flex justify-end space-x-2">
                    <button-secondary
                        @click="closeTagSlideout"
                    >
                        {{ _mft('shared:cancel') }}
                    </button-secondary>
                    <button-success
                        :disabled="savingTag || tagForm.name.trim() === ''"
                        form="tag-form"
                        type="submit"
                    >
                        {{ _mft('shared:action.save') }}
                    </button-success>
                </div>
            </template>
        </slideout-with-slots>
    </div>
</template>
